import { all, takeLatest, call, put, fork, select } from 'redux-saga/effects';
import { LOCATION_UPDATE } from '@okapi/router-connect';
import { basketRootSaga, doFetchMeasurementSettings, tintServiceRootSaga } from 'features/basket';
import { rootSaga as shoppingListRootSaga } from 'features/shopping-list';
import { doResetNavigationContext, navigationSaga } from 'features/page-navigation';
import { rootSaga as goodsSupplySaga } from 'features/goods-supply';
import {
  reviewsLikeDislikeSagas,
  reviewsFacetsAndSortingsSaga,
  reviewsSaga,
  REVIEWS_RESET_FILTER_BY_SCORE,
  REVIEWS_RESET_SORTING,
  POST_REVIEW_RESET,
} from 'features/reviews';
import { postReviewSagas } from 'features/reviews/sagas/post-reviews';
import { reviewsPhotosSaga } from 'features/reviews-photos/sagas/reviews-photos';
import { DATA_FETCHING_COMPLETED, DATA_FETCHING_RUNNING } from 'features';
import { photoUploadSagas } from 'features/reviews/sagas/upload-photos';
import { runGaDispatcher } from 'features/ga-events/dispatch-ga-event';
import { businessUnits } from 'features/bisiness-units/sagas';
import { runErrorDetector } from 'features/errors-hook';
import { getProductId as getBeforeLocationUpdateId } from 'features/product-info';
import { getArticle, getIsBot, getIsSetPath } from 'common/selectors';
import { doPopulateSEOTemplates } from 'features/page-model/ducks/get-model';
import { variantsSaga } from 'features/variants/sagas';
import { EffectR } from 'types/utils';
import { doFetchRelatedServices } from 'features/related-services';
import { runGetProductMerchantInfo } from 'features/product-info/ducks/product-merchant-info';
import { getCalculatorParameters } from 'features/product-info/ducks/product-calculator-parameters';
import { getLaminateConfiguration } from 'features/configurations/ducks/laminate';
import { START_TINT_SERVICE } from 'features/product-info/ducks/product-tint-service';
import { startTintServices } from 'features/basket/ducks/service-basket/service-basket';
import { watchCompareListActions } from 'features/products-compare/ducks/products-compare';
import { fetchState } from './fetch-state-saga';
import { runLoyaltyServices } from 'features/product-loyalty-rewards/sagas';
import { clearLoyaltyRewardsAction } from '../../features/product-loyalty-rewards/actions';
import { clientVerificationSaga } from 'features/client-verification/ducks/client-verification';
import { trustDocumentsSaga } from 'features/product-quality-documents/ducks/saga';
import { RESET_TRUST_DOCUMENTS } from 'features/product-quality-documents/ducks/actions';

export function* rootSaga(): Generator {
  if (!((yield select(getIsBot)) as EffectR.Select<typeof getIsBot>)) {
    yield all([takeLatest(LOCATION_UPDATE, onLocationUpdate), call(runPageServices)]);
  }
}

export function* onLocationUpdate() {
  const beforeId = (yield select(getBeforeLocationUpdateId)) as string | undefined;
  const afterId = (yield select(getArticle)) as string;

  // hash updates trigger LOCATION_UPDATE too so we have to check id changes
  if (afterId !== beforeId) {
    yield call(updateStoreState);
  }
}

export function* runPageServices() {
  const isSetPath = (yield select(getIsSetPath)) as ReturnType<typeof getIsSetPath>;

  const calls = [
    // basket control
    call(runBasketService),
    // pages sections control
    call(navigationSaga),

    call(runGaDispatcher),

    call(runErrorDetector),

    call(runGetProductMerchantInfo),

    call(trustDocumentsSaga),

    call(variantsSaga),

    call(doFetchRelatedServices),

    call(runTintService),

    call(runLoyaltyServices),

    call(calculatorParametersAndProjectConfigurationSaga),

    call(goodsSupplySaga),
  ];

  if (!isSetPath) {
    calls.push(
      call(runWishlistService),
      call(reviewsControlSaga),
      call(doFetchMeasurementSettings),
      call(watchCompareListActions),
      call(businessUnits),
    );
  }

  yield all(calls);
}

export function* runBasketService() {
  yield fork(basketRootSaga);
}

export function* runWishlistService() {
  yield fork(shoppingListRootSaga);
}

export function* reviewsControlSaga() {
  yield put(POST_REVIEW_RESET());
  yield all([
    call(reviewsSaga),
    call(reviewsFacetsAndSortingsSaga),
    call(reviewsLikeDislikeSagas),
    call(postReviewSagas),
    call(photoUploadSagas),
    call(reviewsPhotosSaga),
    call(clientVerificationSaga),
  ]);
}

export function* updateStoreState() {
  yield put(DATA_FETCHING_RUNNING());
  yield put(clearLoyaltyRewardsAction());
  yield put(RESET_TRUST_DOCUMENTS());

  yield all([put(REVIEWS_RESET_FILTER_BY_SCORE()), put(REVIEWS_RESET_SORTING()), put(POST_REVIEW_RESET())]);
  yield call(fetchState);
  yield call(getCalculatorParameters);
  yield call(getLaminateConfiguration);
  yield call(doResetNavigationContext);
  yield call(doPopulateSEOTemplates);

  yield call(startTintServices);

  yield put(DATA_FETCHING_COMPLETED());
}

export function* runTintService() {
  yield fork(tintServiceRootSaga);
  yield put(START_TINT_SERVICE());
}

export function* calculatorParametersAndProjectConfigurationSaga() {
  yield call(getCalculatorParameters);
  yield call(getLaminateConfiguration);
}
